import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "c-workflow-gmp__inner" }
const _hoisted_3 = {
  key: 0,
  class: "c-workflow-gmp__platform"
}
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = { class: "c-workflow-gmp__platform-track" }
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = { class: "c-workflow-gmp__stage-label" }
const _hoisted_8 = ["src", "alt"]
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = {
  key: 0,
  class: "c-workflow-gmp__system-title"
}
const _hoisted_11 = { class: "c-workflow-gmp__system-layout" }
const _hoisted_12 = {
  key: 0,
  class: "c-workflow-gmp__system-label"
}
const _hoisted_13 = { class: "c-workflow-gmp__transfer" }
const _hoisted_14 = ["aria-label"]
const _hoisted_15 = {
  key: 0,
  class: "c-workflow-gmp__transfer-label"
}
const _hoisted_16 = {
  key: 1,
  class: "c-workflow-gmp__gene"
}
const _hoisted_17 = {
  key: 2,
  class: "c-workflow-gmp__helpers"
}
const _hoisted_18 = ["innerHTML"]
const _hoisted_19 = {
  key: 3,
  class: "c-workflow-gmp__cells"
}
const _hoisted_20 = ["innerHTML"]
const _hoisted_21 = ["src", "alt"]
const _hoisted_22 = {
  key: 4,
  class: "c-workflow-gmp__vector"
}
const _hoisted_23 = ["src", "alt"]
const _hoisted_24 = ["innerHTML"]

import { computed } from 'vue';
import type {
  WorkflowGMPGradeStageFields,
  WorkflowGMPHelperItemFields,
  WorkflowGMPPlatformRowFields,
  WorkflowGMPPlatformStepFields,
  WorkflowGMPProps,
  WorkflowGMPTimelineItemFields,
} from '@/types/components/WorkflowGMP';
import type { ImageField, ScItem } from '@/types/sitecore';
import utils from '@/utils';

type WorkflowGMPTimelineItem = ScItem<WorkflowGMPTimelineItemFields> & {
  fields: WorkflowGMPTimelineItemFields;
};
type WorkflowGMPGradeStage = ScItem<WorkflowGMPGradeStageFields> & {
  fields: WorkflowGMPGradeStageFields;
};
type WorkflowGMPHelperItem = ScItem<WorkflowGMPHelperItemFields> & {
  fields: WorkflowGMPHelperItemFields;
};
type WorkflowGMPPlatformRow = ScItem<WorkflowGMPPlatformRowFields> & {
  fields: WorkflowGMPPlatformRowFields;
};
type WorkflowGMPPlatformStep = ScItem<WorkflowGMPPlatformStepFields> & {
  fields: WorkflowGMPPlatformStepFields;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkflowGMP',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-workflow-gmp'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
const isTimeline = computed(() => {
  const value = fields.value?.IsTimeline?.value;
  return value === undefined ? true : value === true || value === 'true' || value === '1';
});
const isPlatformWorkflow = computed(() => !isTimeline.value);

const platformWorkflowRows = computed<WorkflowGMPPlatformRow[]>(() => {
  const rows = utils.sc.getFieldObj(fields.value, 'platformRows');
  return Array.isArray(rows)
    ? rows.filter((row): row is WorkflowGMPPlatformRow => Boolean(row?.fields?.label?.value))
    : [];
});

const timelineItems = computed<WorkflowGMPTimelineItem[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'timelineItems');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowGMPTimelineItem => Boolean(item?.fields?.label?.value))
    : [];
});

const gradeStages = computed<WorkflowGMPGradeStage[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'gradeStages');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowGMPGradeStage => Boolean(item?.fields?.text?.value))
    : [];
});

const helperItems = computed<WorkflowGMPHelperItem[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'helperItems');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowGMPHelperItem => Boolean(item?.fields?.title?.value))
    : [];
});

const gradeTrackCount = computed(() =>
  gradeStages.value.reduce((max, stage) => {
    const gridStart = Number(stage.fields.gridStart?.value ?? 0);
    const gridSpan = Number(stage.fields.gridSpan?.value ?? 1);

    return Number.isFinite(gridStart) && gridStart > 0 && Number.isFinite(gridSpan)
      ? Math.max(max, gridStart + Math.max(1, gridSpan) - 1)
      : max;
  }, 1)
);

const timelineStyle = computed(() => ({
  '--workflow-gmp-count': `${Math.max(timelineItems.value.length, 1)}`,
  '--workflow-gmp-grade-count': `${Math.max(gradeTrackCount.value, 1)}`,
}));

const hasSystemContent = computed(() => {
  if (isPlatformWorkflow.value) {
    return (
      fields.value?.systemTitle?.value ||
      fields.value?.vectorLabel?.value ||
      getImageSrc(fields.value?.vectorImage)
    );
  }

  return (
    fields.value?.systemTitle?.value ||
    fields.value?.systemLabel?.value ||
    fields.value?.transferTitle?.value ||
    helperItems.value.length ||
    fields.value?.cellLineText?.value ||
    fields.value?.vectorLabel?.value
  );
});

const COLOR_VALUE_PATTERN = /^(#|rgb\(|rgba\(|hsl\(|hsla\(|var\()/i;

const getColorValue = (value?: string) => {
  const color = value?.trim();
  return color && COLOR_VALUE_PATTERN.test(color) ? color : '';
};

const getImageValue = (image?: ImageField) => image?.value || {};
const getImageSrc = (image?: ImageField) => getImageValue(image).src || '';
const getImageAlt = (image: ImageField | undefined, fallback: string) =>
  getImageValue(image).alt || fallback;

const getDotStyle = (item: WorkflowGMPTimelineItem) => {
  const color = getColorValue(item.fields.dotColor?.value);
  return color ? { '--dot-bg': color } : {};
};

const getPlatformSteps = (row: WorkflowGMPPlatformRow) => {
  const steps = utils.sc.getFieldObj(row.fields, 'steps');
  return Array.isArray(steps)
    ? steps.filter((step): step is WorkflowGMPPlatformStep => Boolean(step?.fields?.text?.value))
    : [];
};

const PLATFORM_ROW_VARIANTS = ['scope', 'practice', 'highlight'];

const getPlatformVariant = (index: number) => PLATFORM_ROW_VARIANTS[index] || 'practice';

const getPlatformStepStyle = (step: WorkflowGMPPlatformStep) => ({
  '--platform-step-span': String(toPositiveNumber(step.fields.span?.value, 1)),
});

const toPositiveNumber = (value: number | string | undefined, fallback: number) => {
  const numericValue = Number(value ?? fallback);
  return Number.isFinite(numericValue) ? Math.max(1, numericValue) : fallback;
};

const getGradeStyle = (stage: WorkflowGMPGradeStage) => {
  const gridStart = Number(stage.fields.gridStart?.value ?? 0);
  const gridSpan = toPositiveNumber(stage.fields.gridSpan?.value, 1);

  if (!Number.isFinite(gridStart) || gridStart <= 0) {
    return {};
  }

  return {
    '--grade-start': `${gridStart}`,
    '--grade-span': `${gridSpan}`,
  };
};

const getHelperStyle = (item: WorkflowGMPHelperItem) => {
  const color = getColorValue(item.fields.tone?.value);
  if (!color) return {};

  return {
    '--plasmid-color': color,
    '--plasmid-flag': color.toLowerCase() === '#eeeeee' ? '#3d3d3f' : '#6d9272',
  };
};

const getHelperClass = (item: WorkflowGMPHelperItem) => {
  const tone = item.fields.tone?.value?.trim().toLowerCase() || 'green';
  return getColorValue(tone) ? '' : 'c-workflow-gmp__plasmid--' + tone;
};

return (_ctx: any,_cache: any) => {
  const _component_JssRichText = _resolveComponent("JssRichText")!
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (fields.value)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value),
        id: __props.rendering?.dataSource
      }, [
        _createVNode(_component_ContentGrid, {
          "desktop-default-grid": [2, 20],
          "tablet-default-grid": [1, 14],
          "mobile-default-grid": [1, 14]
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_JssRichText, {
                class: "c-workflow-gmp-title",
                field: fields.value?.title
              }, null, 8, ["field"]),
              (isPlatformWorkflow.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(platformWorkflowRows.value, (row, rowIndex) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: row.id || row.fields.label?.value,
                        class: _normalizeClass(["c-workflow-gmp__platform-row", `c-workflow-gmp__platform-row--${getPlatformVariant(rowIndex)}`])
                      }, [
                        _createElementVNode("div", {
                          class: "c-workflow-gmp__platform-label",
                          innerHTML: row.fields.label.value
                        }, null, 8, _hoisted_4),
                        _createElementVNode("div", _hoisted_5, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getPlatformSteps(row), (step) => {
                            return (_openBlock(), _createElementBlock("article", {
                              key: step.id || step.fields.text?.value,
                              class: "c-workflow-gmp__platform-step",
                              style: _normalizeStyle(getPlatformStepStyle(step))
                            }, [
                              _createElementVNode("span", {
                                innerHTML: step.fields.text.value
                              }, null, 8, _hoisted_6)
                            ], 4))
                          }), 128))
                        ])
                      ], 2))
                    }), 128))
                  ]))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    (timelineItems.value.length)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: "c-workflow-gmp__timeline",
                          style: _normalizeStyle(timelineStyle.value)
                        }, [
                          _cache[0] || (_cache[0] = _createElementVNode("span", {
                            class: "c-workflow-gmp__timeline-line",
                            "aria-hidden": "true"
                          }, null, -1)),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timelineItems.value, (item) => {
                            return (_openBlock(), _createElementBlock("article", {
                              key: item.id || item.fields.label?.value,
                              class: "c-workflow-gmp__timeline-item"
                            }, [
                              _createElementVNode("span", _hoisted_7, _toDisplayString(item.fields.label.value), 1),
                              (getImageSrc(item.fields.icon))
                                ? (_openBlock(), _createElementBlock("img", {
                                    key: 0,
                                    class: "c-workflow-gmp__stage-icon",
                                    src: getImageSrc(item.fields.icon),
                                    alt: getImageAlt(item.fields.icon, item.fields.label.value),
                                    loading: "lazy"
                                  }, null, 8, _hoisted_8))
                                : _createCommentVNode("", true),
                              _createElementVNode("span", {
                                class: "c-workflow-gmp__dot",
                                style: _normalizeStyle(getDotStyle(item))
                              }, null, 4)
                            ]))
                          }), 128))
                        ], 4))
                      : _createCommentVNode("", true),
                    (gradeStages.value.length)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 1,
                          class: "c-workflow-gmp__grades",
                          style: _normalizeStyle(timelineStyle.value)
                        }, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(gradeStages.value, (stage) => {
                            return (_openBlock(), _createElementBlock("article", {
                              key: stage.id || stage.fields.text?.value,
                              class: "c-workflow-gmp__grade",
                              style: _normalizeStyle(getGradeStyle(stage)),
                              innerHTML: stage.fields.text.value
                            }, null, 12, _hoisted_9))
                          }), 128))
                        ], 4))
                      : _createCommentVNode("", true)
                  ], 64)),
              (hasSystemContent.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 2,
                    class: _normalizeClass(["c-workflow-gmp__system-card", { 'c-workflow-gmp__system-card--platform': isPlatformWorkflow.value }])
                  }, [
                    (fields.value.systemTitle?.value)
                      ? (_openBlock(), _createElementBlock("h3", _hoisted_10, _toDisplayString(fields.value.systemTitle.value), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_11, [
                      (fields.value.systemLabel?.value)
                        ? (_openBlock(), _createElementBlock("strong", _hoisted_12, _toDisplayString(fields.value.systemLabel.value), 1))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", _hoisted_13, [
                        _createElementVNode("div", {
                          class: "c-workflow-gmp__transfer-visual",
                          "aria-label": 
                  fields.value.transferBadge?.value || fields.value.transferTitle?.value || 'Transfer Plasmid'
                ,
                          role: "img"
                        }, [
                          (fields.value.transferTitle?.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(fields.value.transferTitle.value), 1))
                            : _createCommentVNode("", true)
                        ], 8, _hoisted_14)
                      ]),
                      (fields.value.geneText?.value)
                        ? (_openBlock(), _createElementBlock("strong", _hoisted_16, _toDisplayString(fields.value.geneText.value), 1))
                        : _createCommentVNode("", true),
                      (helperItems.value.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(helperItems.value, (item) => {
                              return (_openBlock(), _createElementBlock("span", {
                                key: item.id || item.fields.title?.value,
                                class: _normalizeClass(["c-workflow-gmp__plasmid", getHelperClass(item)]),
                                style: _normalizeStyle(getHelperStyle(item))
                              }, [
                                _cache[1] || (_cache[1] = _createElementVNode("span", {
                                  class: "c-workflow-gmp__plasmid-flag",
                                  "aria-hidden": "true"
                                }, null, -1)),
                                _createElementVNode("span", {
                                  class: "c-workflow-gmp__plasmid-text",
                                  innerHTML: item.fields.title.value
                                }, null, 8, _hoisted_18)
                              ], 6))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true),
                      _cache[2] || (_cache[2] = _createElementVNode("div", {
                        class: "c-workflow-gmp__bracket",
                        "aria-hidden": "true"
                      }, [
                        _createElementVNode("span", { class: "c-workflow-gmp__bracket-notch" })
                      ], -1)),
                      (
                !isPlatformWorkflow.value &&
                (fields.value.cellLineText?.value || getImageSrc(fields.value.cellLineImage))
              )
                        ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                            _createElementVNode("div", {
                              class: "c-workflow-gmp__cell-text",
                              innerHTML: fields.value.cellLineText?.value
                            }, null, 8, _hoisted_20),
                            (getImageSrc(fields.value.cellLineImage))
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  class: "c-workflow-gmp__cell-image",
                                  src: getImageSrc(fields.value.cellLineImage),
                                  alt: getImageAlt(fields.value.cellLineImage, fields.value.cellLineText?.value || ''),
                                  loading: "lazy"
                                }, null, 8, _hoisted_21))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true),
                      (fields.value.vectorLabel?.value || getImageSrc(fields.value.vectorImage))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                            (getImageSrc(fields.value.vectorImage))
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  class: "c-workflow-gmp__vector-image",
                                  src: getImageSrc(fields.value.vectorImage),
                                  alt: getImageAlt(fields.value.vectorImage, fields.value.vectorLabel?.value || ''),
                                  loading: "lazy"
                                }, null, 8, _hoisted_23))
                              : _createCommentVNode("", true),
                            _createElementVNode("div", {
                              class: "c-workflow-gmp__vector-label",
                              innerHTML: fields.value.vectorLabel?.value
                            }, null, 8, _hoisted_24)
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ], 2))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        })
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})